import { Pen, PlusIcon, RefreshCcw, Search } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import tw from "twin.macro";
import { useBoolean } from "usehooks-ts";

import { Area, Post } from "@/api/areas/types";
import { ModuleCommandEnum } from "@/api/events/modules/command.enum";
import { useSendCommand } from "@/api/modules/mutations";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/accordion";
import { Button } from "@/components/buttons";
import PostErrorModal from "@/components/modals/PostErrorModal";
import { PageContent } from "@/components/page";
import SearchInput from "@/components/SearchInput";
import SortDirectionButton from "@/components/SortDirectionButton";
import SortMenuMd from "@/components/SortMenuMd";
import { BROADCAST_ADDRESS } from "@/constants/roleOptions";
import { useClientContext } from "@/data-loaders/client-data-loader";
import { filterAndSortByString, SortDirection } from "@/utils/searchSort";

import { CreateAreaModaleForm } from "./CreateAreaModale";
import { UpdateAreaModale } from "./UpdateAreaModale";

export const AreasLayout = () => {
  const client = useClientContext();

  const { t } = useTranslation();
  const [openAreaModale, setOpenAreaModale] = useState(false);

  const openNewAreaModale = () => {
    setOpenAreaModale(true);
  };

  const [openItem, setOpenItem] = useState<string | undefined>(
    () => `item-${window.location.hash.replace("#", "")}`
  );

  return (
    <PageContent tw="max-w-7xl">
      <div tw="flex flex-col gap-8 z-10">
        <div tw="flex items-center justify-end">
          <Button secondary onClick={openNewAreaModale} tw="shadow md:-mt-16">
            <PlusIcon tw="w-5 h-5 mr-2" />
            {t("form.addArea.button")}
          </Button>
        </div>
        <div tw="flex flex-col w-full gap-4">
          {client.areas && client.areas.length === 0 ? (
            <div tw="flex items-center justify-center h-96">
              <p tw="text-lg text-gray-500">{t("areas.noPosts")}</p>
            </div>
          ) : (
            client.areas && (
              <div tw="flex flex-col w-full bg-white divide-y divide-gray-100 shadow-sm rounded-xl px-3">
                {client.areas.map((area) => (
                  <Accordion
                    type="single"
                    collapsible
                    value={openItem}
                    onValueChange={setOpenItem}
                    tw="w-full overflow-visible relative"
                    key={area._id}
                  >
                    <AreaInformations area={area} openItem={openItem} />
                  </Accordion>
                ))}
              </div>
            )
          )}
        </div>
      </div>
      <CreateAreaModaleForm
        key={"newArea"}
        open={openAreaModale}
        setOpen={setOpenAreaModale}
        clientId={client._id}
      />
    </PageContent>
  );
};

const AreaInformations = ({
  area,
  openItem,
  ...props
}: {
  area: Area;
  openItem?: string;
}) => {
  const [openUpdateAreaNameModale, setOpenUpdateAreaNameModale] =
    useState(false);
  const openUpdateAreaName = () => {
    setOpenUpdateAreaNameModale(true);
  };
  const { mutate: sendCommand, isPending: isRefreshing } = useSendCommand({
    areaId: area._id,
  });

  return (
    <AccordionItem value={`item-${area._id}`} {...props}>
      <div
        tw="sticky top-0 "
        css={[openItem === `item-${area._id}` && tw`border-b border-gray-100`]}
      >
        {/* XXX: DOM nesting error. The AccordionTrigger is a button, we should not use a button inside a button */}
        <AccordionTrigger tw="bg-white z-10">
          <div tw="flex w-full justify-between items-center py-2">
            <div tw="flex items-center px-2 py-1 gap-4 rounded-lg !z-50 cursor-pointer capitalize text-gray-800">
              {area.name}
            </div>
            <div tw="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  sendCommand({
                    destinationAddress: BROADCAST_ADDRESS.toString(),
                    command: ModuleCommandEnum.GET_STATUS,
                  });
                }}
                tw="flex-1 h-12 flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-semibold"
              >
                <RefreshCcw
                  tw="w-5 h-5"
                  css={isRefreshing && tw`animate-spin`}
                />
              </button>
              <button
                className="group"
                tw="flex justify-center grow-0 shrink-0 w-14 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  return openUpdateAreaName();
                }}
              >
                <Pen tw="transition-all h-4 group-hover:h-5 opacity-70 group-hover:opacity-100 w-auto shrink-0" />
              </button>
            </div>
          </div>
        </AccordionTrigger>
      </div>

      <AccordionContent tw="py-3">
        <div tw="flex flex-col gap-6 p-1">
          <AreaGateways gateways={area.gateways} />
          <AreaPosts areaId={area._id} posts={area.posts} />
        </div>
      </AccordionContent>

      <UpdateAreaModale
        key={"update-" + area?._id}
        open={openUpdateAreaNameModale}
        setOpen={setOpenUpdateAreaNameModale}
        area={(area as Area) || ""}
      />
    </AccordionItem>
  );
};

const AreaGateways = ({ gateways }: { gateways: Area["gateways"] }) => {
  const { t } = useTranslation();

  if (gateways.length === 0) {
    return <p tw="italic">{t("areas.noGateways")}</p>;
  }

  return (
    <div>
      {/* <div tw=" py-1 text-base flex items-center gap-2">
        <span>Gateways</span>{" "}
        <div tw="text-white bg-primary-700 w-5 h-5 rounded-full flex items-center justify-center text-xs">
          {gateways.length}
        </div>
      </div> */}
      <div tw="w-full grid grid-cols-3 text-gray-500 font-semibold  px-4 py-0.5 bg-secondary-50 rounded-xl">
        <span>Nom de la gateway</span>
        <span>Latitude</span>
        <span>Longitude</span>
      </div>

      <div tw="px-4 divide-y">
        {gateways.map((gateway) => (
          <div key={gateway.name} tw="w-full grid grid-cols-3 py-0.5">
            <span>{gateway.name}</span>
            <span>{gateway.latitude}</span>
            <span>{gateway.longitude}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const AreaPosts = ({
  areaId,
  posts,
}: {
  areaId: string;
  posts: Area["posts"];
}) => {
  const { t } = useTranslation();
  const [addressQuery, setAddressQuery] = useState("");
  const [sortId, setSortId] = useState("wirepasAddress-asc");

  if (posts.length === 0) {
    return <p tw="italic pl-3">{t("areas.noPosts")}</p>;
  }

  const visibleModules = useMemo(() => {
    const sortBy = sortId.startsWith("wirepasAddress")
      ? "wirepasAddress"
      : "wirepasAddress";
    const addressSortDirection: SortDirection = sortId.endsWith("-desc")
      ? "desc"
      : "asc";

    const getValue = (p: Post) => {
      if (sortBy === "wirepasAddress") return String(p.address);
      return String(p.address);
    };

    return filterAndSortByString(posts, {
      query: addressQuery,
      getValue,
      direction: addressSortDirection,
      locale: "fr",
    });
  }, [addressQuery, posts, sortId]);

  return (
    <div tw="flex flex-col gap-4">
      <div tw="flex flex-col gap-2 mb-2">
        <div tw="flex flex-col md:flex-row md:items-center gap-2">
          <div tw="flex items-center gap-2 w-full md:max-w-2xl">
            <SearchInput
              value={addressQuery}
              onChange={setAddressQuery}
              placeholder="Rechercher par adresse wirepas…"
            />

            <div tw="hidden md:block">
              <SortMenuMd
                options={[
                  { id: "wirepasAddress-asc", label: "Adresse ↑" },
                  {
                    id: "wirepasAddress-desc",
                    label: "Adresse ↓",
                  },
                ]}
                value={sortId}
                onChange={setSortId}
                buttonLabel="Tri "
              />
            </div>

            <SortDirectionButton
              tw="md:hidden"
              direction={sortId.endsWith("-desc") ? "desc" : "asc"}
              onToggle={() =>
                setSortId((prev) =>
                  prev.endsWith("-desc")
                    ? prev.replace(/-desc$/, "-asc")
                    : prev.replace(/-asc$/, "-desc")
                )
              }
              ariaLabelAsc="Trier par adresse (ascendant)"
              ariaLabelDesc="Trier par adresse (descendant)"
            />
          </div>
        </div>

        {(addressQuery.trim().length > 0 ||
          visibleModules.length !== posts.length) && (
          <div tw="text-xs text-gray-500 px-1">
            {visibleModules.length} / {posts.length}
          </div>
        )}
      </div>

      <div>
        <div tw="w-full grid grid-cols-2 text-gray-500 font-semibold px-4 py-0.5 bg-primary-50 rounded-xl">
          <span tw="col-span-1">Adresse wirepas ({visibleModules.length})</span>
          <span tw="col-span-1" />
        </div>

        <div tw=" divide-y">
          {visibleModules.map((post) => (
            <PostListItem key={post.address} areaId={areaId} post={post} />
          ))}
        </div>
      </div>
    </div>
  );
};

const goToPostDetails = ({
  navigate,
  clientId,
  areaId,
  post,
}: {
  navigate: NavigateFunction;
  clientId: string;
  areaId: string;
  post: Post;
}) => {
  navigate(`/clients/${clientId}/areas/${areaId}/post/${post.address}`);
};

const PostListItem = ({ areaId, post }: { areaId: string; post: Post }) => {
  const client = useClientContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { value: isOpenError, setFalse: closeError } = useBoolean(false);

  return (
    <Fragment>
      <div
        key={post.address}
        tw="w-full grid grid-cols-2 items-center py-0.5 px-4"
        css={[post.status === "problem" && tw`bg-orange-50`]}
      >
        <span tw="col-span-1">{post.address}</span>
        <div tw="flex w-full justify-end py-1">
          <Button
            green
            tw="px-3 py-0.5 w-fit text-xs"
            onClick={() =>
              goToPostDetails({ navigate, clientId: client._id, areaId, post })
            }
            data-tooltip-id={`tooltip-show-${post.address}`}
            data-tooltip-content={t("postList.show")}
          >
            <Search tw="size-4" />
            <Tooltip
              id={`tooltip-show-${post.address}`}
              place="top"
              style={{
                backgroundColor: "#ffffff",
                color: "#03301c",
                fontSize: "0.75rem",
                border: 1,
              }}
            />
          </Button>
        </div>
      </div>

      <PostErrorModal
        key={areaId + post.name + "errormodal"}
        areaId={areaId}
        post={post}
        onClose={closeError}
        open={isOpenError}
      />
    </Fragment>
  );
};
